/**
 * useGoogleDrive – Google OAuth + Drive API helper
 *
 * Uses Google Identity Services (GIS) for OAuth token acquisition.
 * All Drive API calls are made directly via fetch (no gapi.client needed).
 * Google Picker is loaded via gapi for folder selection UI.
 */

import { useCallback, useRef, useState } from "react";

const CLIENT_ID = import.meta.env["VITE_GOOGLE_CLIENT_ID"] as string;
const API_KEY   = import.meta.env["VITE_GOOGLE_API_KEY"] as string;
const SCOPE     = "https://www.googleapis.com/auth/drive";

// ── Token cache ─────────────────────────────────────────────────────────────
// Module-level cache for the live page session; mirrored in localStorage so
// that the ~1h-lived GIS token survives a browser reload. Without the mirror
// the user was re-prompted by Google every refresh.
const STORAGE_KEY = "kra_google_oauth_v1";
let _cachedToken: string | null = null;
let _tokenExpiresAt: number = 0; // epoch ms
let _tokenClient: GisTokenClient | null = null;

function readPersisted(): { token: string; expiresAt: number } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token: string; expiresAt: number };
    if (!parsed?.token || typeof parsed.expiresAt !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function getCachedToken(): string | null {
  const now = Date.now();
  // 1. In-memory cache is fastest
  if (_cachedToken && now < _tokenExpiresAt - 60_000) return _cachedToken;
  // 2. Rehydrate from localStorage (survives reload)
  const persisted = readPersisted();
  if (persisted && now < persisted.expiresAt - 60_000) {
    _cachedToken = persisted.token;
    _tokenExpiresAt = persisted.expiresAt;
    return persisted.token;
  }
  // Expired / absent — clean up so we don't keep retrying
  if (persisted) localStorage.removeItem(STORAGE_KEY);
  return null;
}

function setCachedToken(token: string) {
  // GIS tokens last ~1h; cache for 55min to stay safely under expiry.
  const expiresAt = Date.now() + 55 * 60 * 1000;
  _cachedToken = token;
  _tokenExpiresAt = expiresAt;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, expiresAt }));
  } catch {
    /* storage quota / disabled — in-memory is still valid */
  }
}

/** Public: wipe the cached Google token (e.g. on app logout). */
export function clearGoogleAuth(): void {
  _cachedToken = null;
  _tokenExpiresAt = 0;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// ── Type declarations for Google APIs ────────────────────────────────────────

interface GisTokenClient {
  requestAccessToken: (opts?: { prompt?: string }) => void;
}

interface GisTokenClientConfig {
  client_id: string;
  scope: string;
  callback: (r: { access_token?: string; error?: string }) => void;
  error_callback?: (err: { type?: string; message?: string }) => void;
}

interface PickerData {
  action: string;
  docs?: Array<{ id: string; name: string }>;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (cfg: GisTokenClientConfig) => GisTokenClient;
        };
      };
      picker: {
        PickerBuilder: new () => {
          addView(v: unknown): unknown;
          setOAuthToken(t: string): unknown;
          setDeveloperKey(k: string): unknown;
          setCallback(cb: (d: PickerData) => void): unknown;
          build(): { setVisible(v: boolean): void };
        };
        DocsView: new () => {
          setMimeTypes(m: string): unknown;
          setSelectFolderEnabled(b: boolean): unknown;
        };
        ViewId: { FOLDERS: string };
        Action: { PICKED: string; CANCEL: string };
      };
    };
    gapi?: { load: (lib: string, cb: () => void) => void };
  }
}

// ── Script loader ─────────────────────────────────────────────────────────────

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const el = Object.assign(document.createElement("script"), {
      src,
      async: true,
      defer: true,
    });
    el.onload  = () => resolve();
    el.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(el);
  });
}

// ── Drive REST helpers ────────────────────────────────────────────────────────

async function driveGet<T>(
  path: string,
  token: string,
  params?: Record<string, string>
): Promise<T> {
  const url = new URL(`https://www.googleapis.com/drive/v3${path}`);
  // Always opt into Shared Drive (Team Drive) support. Without these, the
  // Drive API returns 404 "File not found" for a folder/file that lives in a
  // Shared Drive even though the user can open it in the browser. They are
  // no-ops for ordinary My Drive content, so it's safe to set them everywhere.
  url.searchParams.set("supportsAllDrives", "true");
  if (params && "q" in params) {
    // includeItemsFromAllDrives is only valid on files.list (a `q` query).
    url.searchParams.set("includeItemsFromAllDrives", "true");
  }
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const resp = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (resp.ok) return resp.json() as Promise<T>;

  // A 401 means the cached OAuth token is stale/invalid (GIS tokens can die
  // before our 55-min cache window). Drop it so the next attempt re-auths with
  // a fresh token instead of reusing the dead one.
  if (resp.status === 401) clearGoogleAuth();

  // Fallback: a folder shared "Anyone with the link" reads fine with the API
  // key (no per-account context) even when the OAuth token is missing/stale
  // (401) or the folder isn't in the user's My Drive (403/404 — e.g. customer
  // folders that store photos directly with no "Compressed File" subfolder,
  // the only case that calls files.get on the shared root). Retry with the key.
  // Genuinely private content 404s on the key too → original error surfaces.
  if ((resp.status === 401 || resp.status === 403 || resp.status === 404) && API_KEY) {
    url.searchParams.set("key", API_KEY);
    const keyResp = await fetch(url.toString());
    if (keyResp.ok) return keyResp.json() as Promise<T>;
  }
  throw new Error(`Drive ${resp.status}: ${await resp.text()}`);
}

async function drivePost<T>(path: string, token: string, body: unknown): Promise<T> {
  // supportsAllDrives lets copy/create/move operations target Shared Drives.
  const resp = await fetch(
    `https://www.googleapis.com/drive/v3${path}?supportsAllDrives=true`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  if (!resp.ok) throw new Error(`Drive ${resp.status}: ${await resp.text()}`);
  return resp.json() as Promise<T>;
}

// ── Public utilities ──────────────────────────────────────────────────────────

export interface DriveFolder { id: string; name: string }
export interface DriveFile   { id: string; name: string }

export interface CopyProgress {
  total: number;
  done: number;
  currentFile: string;
}

/** Extract Google Drive folder ID from any Drive URL */
export function extractFolderIdFromUrl(url: string): string | null {
  const match = url.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  return match?.[1] ?? null;
}

/** Fetch a single folder's metadata */
export async function getFolder(folderId: string, token: string): Promise<DriveFolder> {
  return driveGet<DriveFolder>(`/files/${folderId}`, token, { fields: "id,name" });
}

export interface DriveFolderWithParent {
  id: string;
  name: string;
  parentId: string | null;
  parentName: string | null;
}

/**
 * Fetch a folder + its first parent's name. Used by Bulk Import to resolve
 * the kimono category from the parent folder (e.g. parent = "06. Furisode"
 * → kimono_tag = "furisode").
 *
 * Returns parentName=null gracefully if the folder is at the Drive root or
 * the parent fetch fails (e.g. shared-with-link parent the user can't see).
 */
export async function getFolderWithParentName(
  folderId: string,
  token: string,
): Promise<DriveFolderWithParent> {
  const f = await driveGet<{ id: string; name: string; parents?: string[] }>(
    `/files/${folderId}`,
    token,
    { fields: "id,name,parents" },
  );
  const parentId = f.parents?.[0] ?? null;
  if (!parentId) return { id: f.id, name: f.name, parentId: null, parentName: null };
  try {
    const parent = await getFolder(parentId, token);
    return { id: f.id, name: f.name, parentId, parentName: parent.name };
  } catch {
    return { id: f.id, name: f.name, parentId, parentName: null };
  }
}

/**
 * List immediate subfolders of a Drive folder, sorted by name.
 *
 * Drive's default order tracks "recently touched", so the same folder list
 * reshuffles itself between visits — useless for finding "06. Furisode cao
 * cap 2" by eye. Numeric collation keeps the "01." … "09." prefixes in order.
 */
export async function listSubfolders(folderId: string, token: string): Promise<DriveFolder[]> {
  const results: DriveFolder[] = [];
  let pageToken: string | undefined;
  do {
    const params: Record<string, string> = {
      q: `'${folderId}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`,
      fields: "files(id,name),nextPageToken",
      pageSize: "1000",
    };
    if (pageToken) params["pageToken"] = pageToken;
    const data = await driveGet<{ files: DriveFolder[]; nextPageToken?: string }>(
      "/files", token, params
    );
    results.push(...data.files);
    pageToken = data.nextPageToken;
  } while (pageToken);
  return results.sort((a, b) =>
    a.name.localeCompare(b.name, "vi", { numeric: true, sensitivity: "base" }),
  );
}

/** List all files (non-folder) in a Drive folder, handling pagination */
export async function listFiles(folderId: string, token: string): Promise<DriveFile[]> {
  const results: DriveFile[] = [];
  let pageToken: string | undefined;
  do {
    const params: Record<string, string> = {
      q: `'${folderId}' in parents and mimeType!='application/vnd.google-apps.folder' and trashed=false`,
      fields: "files(id,name),nextPageToken",
      pageSize: "1000",
    };
    if (pageToken) params["pageToken"] = pageToken;
    const data = await driveGet<{ files: DriveFile[]; nextPageToken?: string }>(
      "/files", token, params
    );
    results.push(...data.files);
    pageToken = data.nextPageToken;
  } while (pageToken);
  return results;
}

/** Create a folder in Drive, returns new folder ID */
export async function createFolder(name: string, parentId: string, token: string): Promise<string> {
  const data = await drivePost<{ id: string }>("/files", token, {
    name,
    mimeType: "application/vnd.google-apps.folder",
    parents: [parentId],
  });
  return data.id;
}

/** Rename a Drive file or folder */
export async function renameDriveItem(
  fileId: string,
  newName: string,
  token: string
): Promise<void> {
  const resp = await fetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}`,
    {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName }),
    }
  );
  if (!resp.ok) throw new Error(`Drive rename failed: ${resp.status}`);
}

/** Copy a single file into a parent folder */
export async function copyFile(
  fileId: string,
  name: string,
  parentId: string,
  token: string
): Promise<string> {
  const resp = await fetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}/copy`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ name, parents: [parentId] }),
    }
  );
  if (!resp.ok) throw new Error(`Copy ${fileId} failed: ${await resp.text()}`);
  return ((await resp.json()) as { id: string }).id;
}

/**
 * Move a folder to a new parent and optionally rename it in the same call.
 * Drive requires the old parent so it can atomically swap parents.
 */
export async function moveFolder(
  folderId: string,
  opts: { newParentId: string; oldParentId: string; newName?: string },
  token: string
): Promise<void> {
  const url = new URL(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(folderId)}`
  );
  url.searchParams.set("addParents", opts.newParentId);
  url.searchParams.set("removeParents", opts.oldParentId);

  const body: Record<string, unknown> = {};
  if (opts.newName) body["name"] = opts.newName;

  const resp = await fetch(url.toString(), {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`Move ${folderId} failed: ${await resp.text()}`);
}

/**
 * Fetch the first image file in a folder and its Drive-hosted thumbnail URL.
 * Returns null if the folder has no images. Used for folder preview cards.
 */
export async function firstImageThumbnail(
  folderId: string,
  token: string
): Promise<string | null> {
  const data = await driveGet<{
    files: Array<{ id: string; thumbnailLink?: string }>;
  }>("/files", token, {
    q: `'${folderId}' in parents and mimeType contains 'image/' and trashed=false`,
    fields: "files(id,thumbnailLink)",
    pageSize: "1",
    orderBy: "name",
  });
  const file = data.files[0];
  if (!file) return null;
  // thumbnailLink is already a full URL but defaults to size=s220. Swap for a
  // larger preview when the UI renders it at card width (~300px).
  if (file.thumbnailLink) return file.thumbnailLink.replace(/=s\d+$/, "=s400");
  return `https://drive.google.com/thumbnail?id=${file.id}&sz=w400`;
}

/**
 * Move a folder to Drive Trash. Reversible (30-day retention), so no
 * confirmation is layered here — callers can decide.
 */
export async function trashFolder(folderId: string, token: string): Promise<void> {
  const resp = await fetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(folderId)}`,
    {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ trashed: true }),
    }
  );
  if (!resp.ok) throw new Error(`Trash ${folderId} failed: ${await resp.text()}`);
}

/** Get public Drive folder URL from folder ID */
export function folderUrl(folderId: string): string {
  return `https://drive.google.com/drive/folders/${folderId}`;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useGoogleDrive() {
  const [accessToken, setAccessToken] = useState<string | null>(() => getCachedToken());
  const resolveRef = useRef<((t: string) => void) | null>(null);
  const rejectRef  = useRef<((e: Error) => void) | null>(null);
  // Silent refresh keeps a separate resolver so an in-flight silent attempt
  // and an explicit user-initiated request can both be tracked safely.
  const silentResolveRef = useRef<((t: string | null) => void) | null>(null);

  /** Request a Google OAuth access token.
   *  Returns cached token immediately if still valid (no popup shown). */
  const requestToken = useCallback((): Promise<string> => {
    // Return cached token if still valid — no popup needed
    const cached = getCachedToken();
    if (cached) return Promise.resolve(cached);

    if (!CLIENT_ID || !API_KEY) {
      return Promise.reject(new Error(
        "Thiếu cấu hình Google (VITE_GOOGLE_CLIENT_ID / VITE_GOOGLE_API_KEY). Liên hệ admin."
      ));
    }

    return new Promise(async (resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current  = reject;

      try {
        await loadScript("https://accounts.google.com/gsi/client");
      } catch {
        reject(new Error("Không thể tải Google SDK")); return;
      }

      const init = () => {
        if (!window.google) { reject(new Error("GIS not available")); return; }
        if (!_tokenClient) {
          _tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: CLIENT_ID,
            scope: SCOPE,
            callback: (r) => {
              // Route callback to whichever flow is in flight. Silent
              // refresh path resolves with `null` on error (no popup
              // fallback); explicit path rejects so the modal can react.
              if (silentResolveRef.current) {
                const cb = silentResolveRef.current;
                silentResolveRef.current = null;
                if (r.error || !r.access_token) {
                  cb(null);
                } else {
                  setCachedToken(r.access_token);
                  setAccessToken(r.access_token);
                  cb(r.access_token);
                }
                return;
              }
              if (r.error || !r.access_token) {
                rejectRef.current?.(new Error(r.error ?? "Auth failed"));
              } else {
                setCachedToken(r.access_token);
                setAccessToken(r.access_token);
                resolveRef.current?.(r.access_token);
              }
            },
            error_callback: (err) => {
              // Non-OAuth errors (popup blocked, network, 3rd-party
              // cookies). The regular `callback` is never fired in this
              // path — without this branch the flow hangs forever.
              if (silentResolveRef.current) {
                const cb = silentResolveRef.current;
                silentResolveRef.current = null;
                cb(null);
                return;
              }
              rejectRef.current?.(new Error(err?.message ?? "Auth failed"));
            },
          });
        }
        // Always show account picker so user can choose the correct Google account
        _tokenClient.requestAccessToken({ prompt: "select_account" });
      };

      // GIS loads async; poll briefly if not ready yet
      if (window.google?.accounts?.oauth2) { init(); return; }
      let attempts = 0;
      const poll = setInterval(() => {
        attempts++;
        if (window.google?.accounts?.oauth2) { clearInterval(poll); init(); }
        else if (attempts > 50) { clearInterval(poll); reject(new Error("GIS timeout")); }
      }, 100);
    });
  }, []);

  /**
   * Attempt a silent token refresh — no UI is shown. Resolves with the
   * fresh token if Google's browser session is still valid for this
   * client, or `null` when interaction is required (user signed out of
   * Google, never authorized this app, account-chooser needed, etc.).
   *
   * Used by the import modal to skip the "Đăng nhập Google" button when
   * the local cache has expired but Google's session is still good. The
   * GIS implicit flow caps access tokens at ~1h, but Google itself keeps
   * the user signed in for much longer — silent refresh exploits that.
   *
   * Never throws. Returns null on any failure so callers can fall back
   * to the explicit `requestToken()` flow.
   */
  const requestTokenSilent = useCallback((): Promise<string | null> => {
    const cached = getCachedToken();
    if (cached) return Promise.resolve(cached);
    if (!CLIENT_ID || !API_KEY) return Promise.resolve(null);

    return new Promise(async (resolve) => {
      // GIS can hang silently on `prompt: "none"` — happens in Safari with
      // ITP, when 3rd-party cookies are blocked, or when the browser's
      // Google session has lapsed in a way that triggers neither callback
      // nor error_callback. Without this hard timeout the modal stays
      // stuck on "Đang kiểm tra phiên Google…" forever.
      const SILENT_TIMEOUT_MS = 6_000;
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const settle = (token: string | null) => {
        if (settled) return;
        settled = true;
        if (timeoutId !== null) clearTimeout(timeoutId);
        if (silentResolveRef.current === settle) silentResolveRef.current = null;
        resolve(token);
      };
      timeoutId = setTimeout(() => settle(null), SILENT_TIMEOUT_MS);

      // If a silent attempt is already in flight, dedupe to it.
      if (silentResolveRef.current) {
        const prev = silentResolveRef.current;
        silentResolveRef.current = (t) => { prev(t); settle(t); };
        return;
      }
      silentResolveRef.current = settle;

      try {
        await loadScript("https://accounts.google.com/gsi/client");
      } catch {
        settle(null);
        return;
      }

      const init = () => {
        if (!window.google) {
          settle(null);
          return;
        }
        if (!_tokenClient) {
          _tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: CLIENT_ID,
            scope: SCOPE,
            callback: (r) => {
              if (silentResolveRef.current) {
                const cb = silentResolveRef.current;
                silentResolveRef.current = null;
                if (r.error || !r.access_token) {
                  cb(null);
                } else {
                  setCachedToken(r.access_token);
                  setAccessToken(r.access_token);
                  cb(r.access_token);
                }
                return;
              }
              // Fall-through to the explicit-flow handlers (shouldn't
              // happen in practice — silent path always sets the silent
              // ref before triggering).
              if (r.error || !r.access_token) {
                rejectRef.current?.(new Error(r.error ?? "Auth failed"));
              } else {
                setCachedToken(r.access_token);
                setAccessToken(r.access_token);
                resolveRef.current?.(r.access_token);
              }
            },
            error_callback: (err) => {
              if (silentResolveRef.current) {
                const cb = silentResolveRef.current;
                silentResolveRef.current = null;
                cb(null);
                return;
              }
              rejectRef.current?.(new Error(err?.message ?? "Auth failed"));
            },
          });
        }
        // `prompt: "none"` does a hidden-iframe refresh against Google's
        // session cookie. Throws (via callback error) if interaction is
        // needed — the modal then shows the manual sign-in button.
        _tokenClient.requestAccessToken({ prompt: "none" });
      };

      if (window.google?.accounts?.oauth2) { init(); return; }
      let attempts = 0;
      const poll = setInterval(() => {
        attempts++;
        if (window.google?.accounts?.oauth2) { clearInterval(poll); init(); }
        else if (attempts > 50) {
          clearInterval(poll);
          settle(null);
        }
      }, 100);
    });
  }, []);

  const LAST_FOLDER_KEY = "kra_last_drive_folder";

  /** Open Google Picker for selecting a Drive folder.
   *  On mobile (iOS/Android) the Picker popup is blocked — falls back to:
   *  1. Last saved folder (auto-select), or
   *  2. Throws "mobile-no-saved-folder" so the modal can show manual input. */
  const showFolderPicker = useCallback((token: string): Promise<DriveFolder> => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      // Google Picker popup is blocked on iOS/Android — use in-app folder browser
      return Promise.reject(new Error("mobile-use-browser"));
    }

    return new Promise(async (resolve, reject) => {
      try {
        await loadScript("https://apis.google.com/js/api.js");
      } catch {
        reject(new Error("Không thể tải Google Picker")); return;
      }

      const openPicker = () => {
        if (!window.google?.picker || !window.gapi) {
          reject(new Error("Picker not available")); return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const g = window.google.picker as any;

        // Restore last used folder so picker opens there directly
        const lastFolderId = localStorage.getItem(LAST_FOLDER_KEY);
        const view = new g.DocsView(g.ViewId.FOLDERS)
          .setMimeTypes("application/vnd.google-apps.folder")
          .setSelectFolderEnabled(true)
          .setIncludeFolders(true);

        if (lastFolderId) {
          view.setParent(lastFolderId);
        }

        const picker = new g.PickerBuilder()
          .addView(view)
          .setOAuthToken(token)
          .setDeveloperKey(API_KEY)
          .setCallback((data: PickerData) => {
            if (data.action === g.Action.PICKED && data.docs?.[0]) {
              const folder = { id: data.docs[0].id, name: data.docs[0].name };
              // Save the PARENT of selected folder so next open lands one level up
              // (e.g. pick "furisode" → save "01. Kimono" → next time opens at 01. Kimono)
              driveGet<{ parents?: string[] }>(`/files/${folder.id}`, token, { fields: "parents" })
                .then((info) => {
                  const parentId = info.parents?.[0];
                  localStorage.setItem(LAST_FOLDER_KEY, parentId ?? folder.id);
                })
                .catch(() => {
                  localStorage.setItem(LAST_FOLDER_KEY, folder.id);
                });
              resolve(folder);
            } else if (data.action === g.Action.CANCEL) {
              reject(new Error("cancelled"));
            }
          })
          .build();
        picker.setVisible(true);
      };

      window.gapi!.load("picker", openPicker);
    });
  }, []);

  return { accessToken, requestToken, requestTokenSilent, showFolderPicker };
}
